// 🔗 외부 웹사이트 열기 공통 유틸 (2026-08-13 — NoticeCard 의 사설 구현을 승격, 링크 모음과 공용).
//   데탑(Tauri/WebView2): 웹뷰 팝업으로 열면 Edge HTTPS-First 가 http 사이트에 "보안 연결을 지원하지
//   않습니다" 경고를 띄움(2026-08-05 사용자) → OS 기본 브라우저로 위임.
//   웹/모바일: 기본 앵커(target=_blank) 그대로 — APK 는 shouldOverrideUrlLoading 이 외부 브라우저로 보냄.

import type { MouseEvent } from 'react';
import { isTauri, isPopoutWindow } from '../desktop/env';
import { isNativeApp } from './platform';
import { isNarrowLayout } from '../hooks/useIsMobile';
import { looksBareHost } from './linkifyPure';

// 🔒 http/https 만 허용 — javascript:/data: 등 스킴 주입(XSS) 차단
// 🔴 스킴 없는 주소 보정 (2026-09-16) — 관리자가 링크칸에 `play.powerlogics.kr` 처럼 스킴을 빼고 적으면
//   종전엔 window.location.origin 기준 '상대경로' 로 해석돼 protocol=https: 검사를 통과한 뒤 원문 그대로
//   반환됐다 → 앱 내부 상대 링크가 되어 데탑은 open_external 이 거부(빈 창), 웹은 SPA 404 로 아무 데도
//   못 갔다("바로가기가 안 열림"). 호스트처럼 생긴 것만 http:// 를 붙여 외부로 보낸다(그룹웨어는 https 미제공).
//   앱 내부 경로(`/projects?id=…`)는 종전대로 그대로 둔다 — isInternalUrl 판정이 그대로 살아야 한다.
export function safeHttpUrl(u?: string): string | undefined {
	if (!u) return undefined;
	const s = u.trim();
	const v = looksBareHost(s) ? `http://${s}` : s;
	try { return /^https?:$/.test(new URL(v, window.location.origin).protocol) ? v : undefined; }
	catch { return undefined; }
}

// 🏠 이 앱 안의 경로인가 — '/projects?id=…' 같은 상대경로나 같은 오리진 주소 (2026-09-02 원격
//   "프로젝트 카드 기한임박 알림의 링크 바로가기가 프로젝트로 이동이 안 되네").
//   원인: 알림 카드가 내부 경로를 **외부 링크로** 열고 있었다 — 데스크톱은 OS 브라우저에 상대경로를
//   넘겨 아무 일도 안 일어나고, 웹/APK 는 새 탭·외부 브라우저로 빠졌다.
//   🖥 데스크톱(Tauri)의 오리진은 tauri.localhost 라 서버가 만든 절대 링크(https://tptalk.topengnet.com/projects?…)가
//   "외부" 로 판정돼 OS 브라우저(웹)로 빠졌다(2026-09-04 사용자 "데탑에서 했는데도 웹으로 가네") → 앱이 붙는 서버 오리진
//   (VITE_TPT_RC_URL)도 내부로 본다. APK 웹뷰는 location.origin 이 곧 서버라 종전대로.
export function appOrigins(): string[] {
	const out = [window.location.origin];
	const envUrl = (import.meta as { env?: Record<string, string> }).env?.VITE_TPT_RC_URL;
	if (envUrl) { try { out.push(new URL(envUrl).origin); } catch { /* 잘못된 env — 무시 */ } }
	return out;
}
export function isInternalUrl(u?: string): boolean {
	if (!u) return false;
	if (/^[a-z][a-z0-9+.-]*:/i.test(u)) {
		try { return appOrigins().includes(new URL(u).origin); } catch { return false; }
	}
	return u.startsWith('/');
}

/** 절대 내부 URL → 앱 라우터 경로(path+query+hash). 상대경로는 그대로. */
export function internalPath(url: string): string {
	if (!/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
	try { const x = new URL(url); return `${x.pathname}${x.search}${x.hash}`; } catch { return url; }
}

/** 내부 경로를 앱 안에서 연다 — 라우터 없이도 되도록 history + popstate 로 알린다(라우터가 이를 듣는다).
 *  🔴 절대 URL 은 경로만 넘긴다 — 데스크톱에서 다른 오리진 URL 로 pushState 하면 SecurityError → location.assign 이 웹뷰를 서버 페이지로 통째로 보냈다. */
/** history.pushState + popstate — 라우터 위임 없이 그대로 이동 (프로젝트 별도 창 규약을 거치지 않는 원시 경로). */
export function pushInternalPath(path: string): void {
	try {
		window.history.pushState({}, '', path);
		window.dispatchEvent(new PopStateEvent('popstate'));
	} catch { window.location.assign(path); }
}

export function navigateInternal(url: string): void {
	const path = internalPath(url);
	// 📋 설문 링크(/forms?id=…) — 화면을 옮기지 않고 앱 전역 설문 팝업으로 (2026-10-07 형 "TASK 알림 클릭하면 설문 팝업").
	//   팝업 호스트(components/forms/FormPopupHost)가 떠 있을 때만, 그리고 /forms 화면 자신에서는 종전대로 이동.
	const formM = /^[/]forms[?](?:[^#]*&)?id=([A-Za-z0-9_-]+)/.exec(path);
	if (formM && !window.location.pathname.startsWith('/forms') && (window as unknown as { __tptFormHost?: boolean }).__tptFormHost) {
		try { window.dispatchEvent(new CustomEvent('tpt:open-form', { detail: { id: formM[1] } })); return; } catch { /* 구형 웹뷰 — 아래 기본 이동 */ }
	}
	// 🗂 프로젝트 링크는 **별도 창 규약**(popout.openProjectsAnywhere, 2026-09-09 "무조건 별도 창") — 채팅 본문 [바로가기]·알림 카드가 여기로 오는데
	//   메인 창을 /projects 로 갈아타거나, 팝아웃이 이미 있으면 아무 일도 안 일어났다 (2026-09-10 사용자 3). 팝아웃 자신(win=1)·앱·좁은 화면은 종전대로 pushState.
	//   🔴 2026-09-11 회귀 수정: 앱·좁은 화면 제외 조건이 주석에만 있고 코드에 없어 openProjectsAnywhere(앱이면 navigateInternal 로 폴백) 와
	//      서로를 되부르는 무한 루프 → 폰에서 프로젝트 [바로가기]·헤더 칩·카드가 전부 무반응이었다. 여기서 앱·좁은 화면은 바로 pushState.
	if (/^\/projects(\?|$)/.test(path)) {
		let inPopout = false;
		try { inPopout = new URLSearchParams(window.location.search).get('win') === '1' && window.location.pathname.startsWith('/projects'); } catch { /* */ }
		if (!inPopout && !isNativeApp() && !isNarrowLayout()) {
			const q = path.split('?')[1] || '';
			void import('../desktop/popout').then((m) => m.openProjectsAnywhere(q.replace(/(^|&)win=1(&|$)/, '$1').replace(/^&|&$/g, '')));
			return;
		}
	}
	// 💬 별도 창(프로젝트·일정·방 창 등) 안의 대화 링크(/chat?rid=&mid=)는 **메인 창**에서 연다 (2026-09-29 형 "별도창에서 바로가기(대화방) 누르면 이동 안 됨").
	//   종전엔 그 별도 창 자신이 /chat 으로 갈아타 — 프로젝트 창이 채팅이 되거나(메인은 그대로), 이미 뜬 ChatPage 는 쿼리를 다시 안 읽어 무반응이었다.
	if (/^\/chat(\?|$)/.test(path) && !isNativeApp() && isPopoutWindow()) {
		let rid = '', mid = '';
		try { const sp = new URLSearchParams(path.split('?')[1] || ''); rid = sp.get('rid') || ''; mid = sp.get('mid') || sp.get('tmid') || ''; } catch { /* */ }
		void import('../desktop/popout').then((m) => {
			if (m.goMainNav('chat', rid ? { roomId: rid, ...(mid ? { messageId: mid } : {}) } : undefined)) return;
			pushInternalPath(path);   // 보낼 메인 창이 없으면(웹 opener 닫힘) 종전대로
		});
		return;
	}
	pushInternalPath(path);
}

// <a href={url} onClick={onExternalClick(url)}> 형태로 사용 — 앵커의 기본 동작을 웹에서는 살린다.
//   내부 경로면 새 탭·외부 브라우저로 보내지 않고 앱 안에서 이동한다.
export function onExternalClick(url: string) {
	return (e: MouseEvent) => {
		if (isInternalUrl(url)) {
			e.preventDefault();
			navigateInternal(url);
			return;
		}
		if (!isTauri()) return;
		e.preventDefault();
		openExternalUrl(url);
	};
}

// 🧾 HTML 본문 컨테이너(dangerouslySetInnerHTML — 공지·프로젝트 글·AI 답변)의 링크 클릭 위임 (2026-09-12 사용자 "프로젝트 글의 link 클릭해도 이동 안 됨").
//   원인: main.tsx 의 문서 레벨 Tauri 앵커 핸들러는 클릭이 document 까지 올라와야 하는데, DetailModal 등 모달 패널이 stopPropagation 을
//   걸어 모달 안 본문의 링크는 거기 닿지 않았다 → 웹뷰가 target=_blank 새 창을 차단해 무반응. 컨테이너 onClick 에 이 함수를 달면
//   내부 주소는 앱 안 이동, 외부는 데탑에서 OS 브라우저(웹은 기본 동작 유지). preventDefault 하므로 문서 핸들러와 이중 처리 없음.
export function onHtmlLinkClick(e: { target: EventTarget | null; defaultPrevented: boolean; preventDefault: () => void }): void {
	if (e.defaultPrevented) return;
	const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
	if (!a || a.hasAttribute('download')) return;
	const href = a.getAttribute('href') || '';
	if (isInternalUrl(href)) { e.preventDefault(); navigateInternal(href); return; }
	// 스킴 빠진 주소(play.powerlogics.kr/…) — 앵커 기본 동작이 '앱 내부 상대경로 이동' 이라 앱을 통째로 떠난다.
	//   플랫폼을 가리지 않고 여기서 가로채 외부로 보낸다 (2026-09-16 공지 바로가기).
	if (looksBareHost(href)) { e.preventDefault(); openExternalUrl(href); return; }
	if (!isTauri()) return;
	if (!/^https?:\/\//i.test(href)) return;
	e.preventDefault();
	openExternalUrl(href);
}

// 앵커가 아닌 곳(버튼·목록 항목)에서 직접 열 때.
export function openExternalUrl(raw: string): void {
	const url = safeHttpUrl(raw);
	if (!url) return;
	if (isTauri()) {
		void import('@tauri-apps/api/core')
			.then(({ invoke }) => invoke('open_external', { url }))
			.catch(() => { try { window.open(url, '_blank', 'noopener'); } catch { /* 팝업 차단 — 무시 */ } });
		return;
	}
	try { window.open(url, '_blank', 'noopener'); } catch { /* 팝업 차단 — 무시 */ }
}
